import * as React from 'react';
import Link from 'next/link';

import { cn } from '@/lib/utils';
import { footerLinkGroups } from '@/components/site/navigation';
import { LEGAL_LINKS } from '@/lib/legal-documents';

type FooterProps = {
  variant?: 'marketing' | 'app' | 'utility';
};

export function Footer({ variant: _variant = 'marketing' }: FooterProps) {
  const maxWidthClass = 'site-shell-wide';
  const primaryLinks = footerLinkGroups.flatMap((group) => group.links);

  return (
    <footer className="px-4 pb-6 pt-1 sm:px-6 lg:px-8">
      <div
        className={cn(
          'mx-auto border-t border-[color:rgba(23,38,59,0.06)]',
          maxWidthClass,
        )}
      >
        <div className="grid gap-[var(--site-footer-main-gap)] py-[var(--site-footer-main-py)] xl:grid-cols-[minmax(0,1.08fr)_minmax(34rem,0.92fr)] xl:items-center xl:gap-x-12">
          <div className="max-w-[46rem] space-y-3 sm:space-y-4">
            <div className="text-[calc(var(--site-footer-link-size)-0.25rem)] font-semibold uppercase tracking-[0.24em] text-[var(--site-ink-subtle)]">
              S-SHOT
            </div>
            <div className="space-y-2">
              <h2 className="text-[length:var(--site-footer-title-size)] font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">
                Парсинг сайтов и структурированные данные без лишнего шума.
              </h2>
              <p className="text-[length:var(--site-footer-copy-size)] leading-6 text-[var(--site-ink-muted)] sm:hidden">
                Парсинг маркетплейсов, шаблоны и датасеты в одном сервисе.
              </p>
              <p className="hidden text-[length:var(--site-footer-copy-size)] leading-6 text-[var(--site-ink-muted)] sm:block">
                Запускайте парсинг сайтов, управляйте шаблонами и собирайте датасеты с маркетплейсов и объявлений.
              </p>
            </div>
            <div className="text-[length:var(--site-footer-copy-size)] text-[var(--site-ink)]">
              Контакты:{' '}
              <a
                href="mailto:info@s-shot.ru"
                className="inline-flex min-h-11 items-center rounded-xl px-1 font-medium text-[var(--site-accent-text)] hover:underline"
              >
                info@s-shot.ru
              </a>
            </div>
          </div>

          <nav className="xl:justify-self-end xl:w-full xl:max-w-[40rem]" aria-label="Footer navigation">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:gap-x-8 sm:gap-y-4 sm:text-base md:grid-cols-3 xl:grid-cols-4">
              {primaryLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex min-h-11 items-center rounded-xl px-3 py-3 text-[length:var(--site-footer-link-size)] leading-6 text-[var(--site-ink-muted)] transition-colors hover:bg-white/55 hover:text-[var(--site-ink-strong)]"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </nav>
        </div>

        <div className="border-t border-[color:var(--site-border-mid)] py-[var(--site-footer-legal-py)]">
          <div className="grid gap-3 sm:gap-4 lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] lg:items-start lg:gap-8">
            <div className="space-y-2 text-[var(--site-ink-muted)]">
              <p className="text-[length:var(--site-footer-copy-size)]">&copy; S-SHOT. Все права защищены.</p>
              <p className="max-w-[15rem] text-xs leading-5 text-[var(--site-ink-subtle)]">
                Документы и условия использования
              </p>
            </div>
            <div className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 sm:gap-x-8 sm:gap-y-4 sm:text-base xl:grid-cols-4 lg:justify-items-end">
              {LEGAL_LINKS.map((link) => (
                <Link
                  key={link.path}
                  href={link.path}
                  className="flex min-h-11 items-center rounded-xl px-3 py-3 text-[length:var(--site-footer-link-size)] leading-6 text-[var(--site-ink-muted)] transition-colors hover:bg-white/55 hover:text-[var(--site-ink-strong)]"
                >
                  {link.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
